/**
 * "AI extraction" sell presets + resolution (LF-4642).
 *
 * The glyph is the Vale mark, not a lucide sparkle (VALE-1232): dropping files
 * here leads straight into the analysing surface, which pulses the same mark,
 * so the two read as one surface rather than two features.
 *
 * Isolated defaults per format so `<FileDropZone sell />` just works with copy
 * tuned to the placement, while an explicit `SellConfig` still overrides. Copy
 * is lifted from the approved muted mock.
 */

import { LayoutGrid, ScanText, Users } from 'lucide-react';
import { ValeAIMarkIcon } from '@/components/icons/vale-ai-mark';
import type { FileDropZoneVariant, SellConfig, SellProp } from './types';

/** Large feature banner — headline, sub-copy, and three benefit chips. */
export const DEFAULT_BANNER_SELL: SellConfig = {
  headline: 'Let Vale AI read your documents for you',
  description:
    'Drop files here and Vale extracts the details automatically, with no manual data entry.',
  benefits: [
    { icon: LayoutGrid, label: 'Auto-categorises every file' },
    { icon: ScanText, label: 'Extracts key details' },
    { icon: Users, label: 'Matches contacts & assets' }
  ],
  icon: ValeAIMarkIcon
};

/** Medium compact strip — headline + one-line benefit. */
export const DEFAULT_COMPACT_SELL: SellConfig = {
  headline: 'Add documents, and Vale AI extracts the details',
  description:
    'Categorised, organised and matched to your matter automatically.',
  icon: ValeAIMarkIcon
};

/**
 * Resolve the `sell` prop into a concrete config (or `null` for neutral).
 * A truthy boolean selects the variant's default; `mini` falls back to the
 * compact default. An explicit config is used as-is with the icon defaulted.
 */
export function resolveSell(
  variant: FileDropZoneVariant,
  sell: SellProp | undefined
): SellConfig | null {
  if (!sell) return null;
  if (sell === true) {
    return variant === 'banner' ? DEFAULT_BANNER_SELL : DEFAULT_COMPACT_SELL;
  }
  return { icon: ValeAIMarkIcon, ...sell };
}
